import { client } from './client'
import type {
  ChainDefinition,
  ChainDefinitionSummary,
  ChainDefinitionFilters,
  CreateChainDefinitionRequest,
  UpdateChainDefinitionRequest,
  ChainYamlSnapshot,
  PaginationParams,
  PagedResult,
  WorkflowValidationResult,
  StartChainRequest,
  StartChainResponse,
  LinkedEntityScope,
} from '@/types'

interface ChainQueryParams {
  search?: string
  isActive?: boolean
  isSystem?: boolean
  linkedEntityType?: LinkedEntityScope
  maxResultCount?: number
  skipCount?: number
}

function buildQueryParams(filters?: ChainDefinitionFilters, pagination?: PaginationParams): ChainQueryParams {
  const params: ChainQueryParams = {}

  if (filters?.search) {
    params.search = filters.search
  }
  if (filters?.status === 'active') {
    params.isActive = true
  } else if (filters?.status === 'inactive') {
    params.isActive = false
  }
  if (filters?.type === 'system') {
    params.isSystem = true
  } else if (filters?.type === 'custom') {
    params.isSystem = false
  }
  if (filters?.linkedEntityScope) {
    params.linkedEntityType = filters.linkedEntityScope
  }

  if (pagination?.maxResultCount) {
    params.maxResultCount = pagination.maxResultCount
  }
  if (pagination?.skipCount) {
    params.skipCount = pagination.skipCount
  }

  return params
}

export const chainsApi = {
  getAll: (projectId: string, filters?: ChainDefinitionFilters, pagination?: PaginationParams) =>
    client.get<PagedResult<ChainDefinitionSummary>>(`/projects/${projectId}/chains`, {
      params: buildQueryParams(filters, pagination),
    }).then(r => r.data),

  getById: (projectId: string, id: string) =>
    client.get<ChainDefinition>(`/projects/${projectId}/chains/${id}`).then(r => r.data),

  create: (projectId: string, data: CreateChainDefinitionRequest) =>
    client.post(`/projects/${projectId}/chains`, data).then(r => r.data),

  update: (projectId: string, id: string, data: UpdateChainDefinitionRequest) =>
    client.put(`/projects/${projectId}/chains/${id}`, data).then(r => r.data),

  duplicate: (projectId: string, id: string) =>
    client.post(`/projects/${projectId}/chains/${id}/duplicate`).then(r => r.data),

  start: (projectId: string, id: string, data: StartChainRequest) =>
    client.post<StartChainResponse>(`/projects/${projectId}/chains/${id}/start`, data).then(r => r.data),

  toggleActive: (projectId: string, id: string) =>
    client.patch(`/projects/${projectId}/chains/${id}/toggle-active`).then(r => r.data),

  delete: (projectId: string, id: string) =>
    client.delete(`/projects/${projectId}/chains/${id}`).then(r => r.data),

  validateYaml: (projectId: string, yamlContent: string) =>
    client.post<WorkflowValidationResult>(`/projects/${projectId}/chains/validate`, { yamlContent }).then(r => r.data),

  getYamlSnapshots: (projectId: string, id: string) =>
    client.get<ChainYamlSnapshot[]>(`/projects/${projectId}/chains/${id}/yaml-snapshots`).then(r => r.data),
}
